import { useMemo } from 'react'
import { FramingStrip } from '@/components/FramingStrip'
import { RivalsBoard } from '@/components/RivalsBoard'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { spaceLeaderboard } from '@/lib/clusters'
import type { Run } from '@/lib/types'

// RivalsLens: per space, who the AI engines name — ranked, with the target brand
// shown in place and highlighted so you can see where you fall. Only spaces where
// at least one rival appeared are shown.
export function RivalsLens({ run }: { run: Run }) {
  const spaces = useMemo(() => {
    const clusters = run.clusters ?? []

    return clusters
      .map((c) => ({
        id: c.id,
        label: c.label,
        board: spaceLeaderboard(c.id, run.perQuery, run.brand),
      }))
      .filter((s) => s.board.some((e) => !e.isBrand)) // has at least one rival
  }, [run.clusters, run.perQuery, run.brand])

  // The first space opens by default so the lens is never an all-collapsed wall.
  const firstOpen = spaces.slice(0, 1).map((s) => s.id)

  return (
    <div>
      <FramingStrip run={run} variant="shared" />

      {spaces.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          No rivals surfaced — the engines didn’t name other brands in these
          spaces.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            When the AI engines answer these intents, which brands do they name
            — and where does{' '}
            <span className="font-medium text-foreground">{run.brand}</span>{' '}
            rank? Expand a space to see the full order.
          </p>

          <Accordion
            type="multiple"
            defaultValue={firstOpen}
            className="flex flex-col gap-2"
          >
            {spaces.map((space) => {
              const brandRank = space.board.findIndex((e) => e.isBrand) + 1

              return (
                <AccordionItem
                  key={space.id}
                  value={space.id}
                  className="rounded-xl border border-border bg-card px-4"
                >
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex w-full items-center gap-3 pr-2 text-left">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {space.label}
                      </span>

                      {/* Where the brand ranks here — the honest one-glance summary. */}
                      <span className="shrink-0 text-xs font-normal text-muted-foreground">
                        you rank{' '}
                        <span className="font-semibold text-foreground tabular-nums">
                          #{brandRank}
                        </span>{' '}
                        of {space.board.length}
                      </span>
                    </span>
                  </AccordionTrigger>

                  <AccordionContent className="pt-1 pb-4">
                    <RivalsBoard board={space.board} />
                  </AccordionContent>
                </AccordionItem>
              )
            })}
          </Accordion>
        </div>
      )}
    </div>
  )
}
